import type { SheetClass } from "./entitySheets";
import {
  EFF_dash,
  EFF_explodeB,
  EFF_explodeSpike,
  EFF_playerTrail,
  EFF_playerTrail2,
  EFF_shotAbsorb,
  SPR_axeShockwave,
  SPR_bossFinal,
  SPR_circuit,
  SPR_circuit2,
  SPR_cleanser,
  SPR_generator,
  SPR_gravityOrb,
  SPR_itemHP,
  SPR_itemPickup,
  SPR_itemXenos,
  SPR_mine,
  SPR_portraitDistort,
} from "../assets";
import { EffectId } from "../ids";

export type { SheetClass };

/**
 * Everything an `Effect` type fixes up front. Anything omitted keeps the
 * field's default, so the table only lists what the original if-chain set.
 * Types that derive a value from the constructor arguments — an angle, a
 * sword-level scale, a tint — do that in `Effect`'s own switch.
 */
export interface EffectSpec {
  /** `null` for the trail types, which pick their sheet from `swordLevel`. */
  readonly sheet: SheetClass | null;
  /** Sheet row width in pixels; divided by `tileSize` to get the row length. */
  readonly rowX: number;
  readonly frameLimit: number;
  readonly tileSize?: number;
  readonly animSpeed?: number;
  readonly lifeLimit?: number;
  readonly fadeRate?: number;
  readonly scaleX?: number;
  readonly scaleY?: number;
  /** Scale `updateTrail` snaps back to; also the initial scale. */
  readonly targetScale?: number;
  readonly rotation?: number;
  readonly alpha?: number;
  readonly moveSpeed?: number;
  readonly smoothing?: boolean;
  readonly additive?: boolean;
  /** Starts fully transparent, for the types that fade themselves up. */
  readonly startHidden?: boolean;
  /** Spawns at a random heading. */
  readonly randomRotation?: boolean;
}

/** Per-type setup, keyed by the `type` passed to `Effect`'s constructor. */
export const EFFECT_SPECS: Readonly<Partial<Record<EffectId, EffectSpec>>> = {
  // player dash streak, aimed by extraValue and grown by sword level
  [EffectId.Dash]: {
    sheet: EFF_dash,
    rowX: 256,
    frameLimit: 16,
    smoothing: true,
    additive: true,
  },
  // player sword trail
  [EffectId.SwordTrail]: {
    sheet: null,
    rowX: 192,
    frameLimit: 12,
    tileSize: 48,
    animSpeed: 1,
  },
  // axe shockwave, aimed and grown by sword level
  [EffectId.AxeShockwave]: {
    sheet: SPR_axeShockwave,
    rowX: 384,
    frameLimit: 6,
    tileSize: 128,
    animSpeed: 3,
    lifeLimit: 50,
    moveSpeed: 2.2,
    smoothing: true,
  },
  [EffectId.SmallHealthPickup]: {
    sheet: SPR_itemHP,
    rowX: 128,
    frameLimit: 16,
    tileSize: 32,
    animSpeed: 2,
    lifeLimit: 1000,
    fadeRate: 25,
  },
  [EffectId.MediumXenosPickup]: {
    sheet: SPR_itemXenos,
    rowX: 192,
    frameLimit: 16,
    tileSize: 48,
    animSpeed: 2,
    lifeLimit: 1500,
    fadeRate: 25,
  },
  [EffectId.LargeXenosPickup]: {
    sheet: SPR_itemXenos,
    rowX: 192,
    frameLimit: 16,
    tileSize: 48,
    animSpeed: 2,
    fadeRate: 25,
    scaleX: 1.5,
    scaleY: 1.5,
    smoothing: true,
  },
  [EffectId.CircuitPickup]: {
    sheet: SPR_circuit,
    rowX: 96,
    frameLimit: 4,
    tileSize: 48,
    animSpeed: 4,
    fadeRate: 25,
  },
  [EffectId.Circuit2Pickup]: {
    sheet: SPR_circuit2,
    rowX: 96,
    frameLimit: 4,
    tileSize: 48,
    animSpeed: 4,
    fadeRate: 25,
  },
  // gravity orb, aimed by extraValue
  [EffectId.GravityOrb]: {
    sheet: SPR_gravityOrb,
    rowX: 64,
    frameLimit: 4,
    tileSize: 32,
    animSpeed: 2,
    fadeRate: 10,
    moveSpeed: 10,
  },
  // explosion spike, aimed by extraValue
  [EffectId.ExplosionSpike]: {
    sheet: EFF_explodeSpike,
    rowX: 256,
    frameLimit: 12,
    tileSize: 64,
    animSpeed: 2,
    fadeRate: 100,
    moveSpeed: 1.5,
    scaleX: 6.5,
    smoothing: true,
    additive: true,
  },
  [EffectId.SmallPickupBurst]: pickupBurst(1.3),
  [EffectId.MediumPickupBurst]: pickupBurst(1.7),
  [EffectId.LargePickupBurst]: pickupBurst(2.1),
  // cut-scene generator, fades itself up
  [EffectId.CutSceneGenerator]: {
    sheet: SPR_generator,
    rowX: 480,
    frameLimit: 20,
    tileSize: 96,
    animSpeed: 2,
    fadeRate: 2,
    scaleX: 2.5,
    scaleY: 2.5,
    smoothing: true,
    startHidden: true,
  },
  [EffectId.CutSceneCore]: {
    sheet: SPR_itemXenos,
    rowX: 192,
    frameLimit: 16,
    tileSize: 48,
    animSpeed: 2,
    fadeRate: 1,
    scaleX: 3.5,
    scaleY: 3.5,
    smoothing: true,
    startHidden: true,
  },
  // cut-scene shine, tinted flat white by the constructor
  [EffectId.CutSceneShine]: {
    sheet: EFF_shotAbsorb,
    rowX: 144,
    frameLimit: 9,
    tileSize: 48,
    animSpeed: 1,
    scaleX: 5,
    scaleY: 5,
    smoothing: true,
  },
  // fortress explosion; the constructor rolls its own scale
  [EffectId.FortressExplosion]: {
    sheet: EFF_explodeB,
    rowX: 128,
    frameLimit: 16,
    tileSize: 32,
    animSpeed: 2,
    lifeLimit: 38,
    fadeRate: 5,
    smoothing: true,
    additive: true,
  },
  [EffectId.FinalBossCutScene]: {
    ...bossFinal(),
    scaleX: 2.4,
    scaleY: 2.4,
    animSpeed: 2,
    startHidden: true,
  },
  // xenos shard, aimed by extraValue and drifting backwards
  [EffectId.XenosShard]: {
    ...bossFinal(),
    scaleX: 0.4,
    scaleY: 0.4,
    animSpeed: 1,
    moveSpeed: -1.5,
  },
  [EffectId.Mine]: {
    sheet: SPR_mine,
    rowX: 288,
    frameLimit: 12,
    tileSize: 48,
    animSpeed: 2,
    smoothing: true,
  },
  [EffectId.Cleanser]: {
    sheet: SPR_cleanser,
    rowX: 320,
    frameLimit: 25,
    tileSize: 64,
    animSpeed: 2,
    scaleX: 0.75,
    scaleY: 0.75,
    smoothing: true,
  },
  [EffectId.SmallFinalBossEffect]: {
    ...bossFinal(),
    scaleX: 0.375,
    scaleY: 0.375,
    animSpeed: 2,
  },
  // cut-scene robot trail, the six-segment twin of type 1
  [EffectId.RobotTrail]: {
    sheet: null,
    rowX: 192,
    frameLimit: 12,
    tileSize: 48,
    animSpeed: 1,
    targetScale: 6,
    smoothing: true,
  },
  // cut-scene robot dash streak, fixed rather than aimed
  [EffectId.RobotDash]: {
    sheet: EFF_dash,
    rowX: 256,
    frameLimit: 16,
    rotation: -90,
    alpha: 0.6,
    scaleX: 6.5,
    scaleY: 7,
    smoothing: true,
    additive: true,
  },
  // portrait glitch; the constructor rolls a random starting life
  [EffectId.PortraitDistortion]: {
    sheet: SPR_portraitDistort,
    rowX: 384,
    frameLimit: 4,
    tileSize: 128,
    animSpeed: 2,
    smoothing: true,
  },
};

/** Sword-trail sheet for types 1 and 40: index 0 is the unpowered sword. */
export const TRAIL_SHEETS: readonly [SheetClass, SheetClass] = [
  EFF_playerTrail,
  EFF_playerTrail2,
];

/** Pickup radius and the action code collecting it returns, by effect type. */
export const ITEM_PICKUPS: Readonly<
  Partial<Record<EffectId, { readonly dist: number; readonly action: number }>>
> = {
  [EffectId.SmallHealthPickup]: { dist: 38, action: 1 },
  [EffectId.MediumXenosPickup]: { dist: 46, action: 2 },
  [EffectId.LargeXenosPickup]: { dist: 60, action: 3 },
  [EffectId.CircuitPickup]: { dist: 46, action: 4 },
  [EffectId.Circuit2Pickup]: { dist: 46, action: 5 },
};

/** The three pickup bursts differ only in scale. */
function pickupBurst(scale: number): EffectSpec {
  return {
    sheet: SPR_itemPickup,
    rowX: 192,
    frameLimit: 16,
    tileSize: 48,
    animSpeed: 2,
    lifeLimit: 31,
    fadeRate: 100,
    scaleX: scale,
    scaleY: scale,
    smoothing: true,
    additive: true,
    randomRotation: true,
  };
}

/** Shared base of the three final-boss cut-scene effects. */
function bossFinal(): EffectSpec {
  return {
    sheet: SPR_bossFinal,
    rowX: 640,
    frameLimit: 23,
    tileSize: 128,
    fadeRate: 1,
    smoothing: true,
  };
}
